import { Eye, EyeOff, Trash2 } from 'lucide-react';
import type { MouseEvent, ReactElement } from 'react';
import { useSiteContextLayers } from '../../hooks/useSiteContextLayers';

interface ContextLayersListProps {
  siteId: string;
  enabled: boolean;
}

/**
 * CONTEXT section layer rows — panel shapes in the active site context frame.
 */
export function ContextLayersList({ siteId, enabled }: ContextLayersListProps): ReactElement | null {
  const { layers, editorReady, selectedShapeId, selectLayer, toggleVisibility, deleteLayer } =
    useSiteContextLayers(siteId, enabled);

  if (!enabled) return null;

  const stopRowSelect = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
  };

  return (
    <div className="vibe-sidebar__layers" data-testid="context-layers-list">
      {layers.length === 0 ? (
        <p className="vibe-sidebar__layers-empty" data-testid="context-layers-empty">
          {editorReady ? 'No layers on this site yet.' : 'Loading canvas layers…'}
        </p>
      ) : (
        <ul className="vibe-sidebar__layers-list" role="list">
          {layers.map((layer) => {
            const isSelected = selectedShapeId === layer.shapeId;

            return (
              <li
                key={layer.shapeId}
                className={`vibe-sidebar__layer-row${isSelected ? ' vibe-sidebar__layer-row--selected' : ''}`}
                data-testid="context-layer-row"
                data-shape-id={layer.shapeId}
                data-selected={isSelected ? 'true' : 'false'}
              >
                <button
                  type="button"
                  className="vibe-sidebar__layer-select"
                  aria-label={`Select ${layer.name}`}
                  aria-pressed={isSelected}
                  data-testid={`context-layer-select-${layer.shapeId}`}
                  onClick={() => selectLayer(layer.shapeId)}
                >
                  <span
                    className={`vibe-sidebar__layer-name${layer.visible ? '' : ' vibe-sidebar__layer-name--hidden'}`}
                    title={layer.name}
                  >
                    {layer.name}
                  </span>
                </button>
                <span className="vibe-sidebar__layer-actions">
                  <button
                    type="button"
                    className="vibe-sidebar__layer-action-btn"
                    aria-label={layer.visible ? `Hide ${layer.name}` : `Show ${layer.name}`}
                    data-testid={`context-layer-visibility-${layer.shapeId}`}
                    onClick={(event) => {
                      stopRowSelect(event);
                      toggleVisibility(layer.shapeId);
                    }}
                  >
                    {layer.visible ? (
                      <Eye className="vibe-sidebar__icon-sm" aria-hidden />
                    ) : (
                      <EyeOff className="vibe-sidebar__icon-sm" aria-hidden />
                    )}
                  </button>
                  <button
                    type="button"
                    className="vibe-sidebar__layer-action-btn vibe-sidebar__layer-action-btn--danger"
                    aria-label={`Delete ${layer.name}`}
                    data-testid={`context-layer-delete-${layer.shapeId}`}
                    onClick={(event) => {
                      stopRowSelect(event);
                      deleteLayer(layer.shapeId, layer.name);
                    }}
                  >
                    <Trash2 className="vibe-sidebar__icon-sm" aria-hidden />
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
